// Generic CRUD page for simple {id, name} recipe lookups.
// Used by two route exports:
//   AdminRecipeOperationalUnits → /api/recipe-operational-units
//   AdminRecipeMetricUnits      → /api/recipe-metric-units
//
// The two pages share identical UX — only label, icon, color, and endpoint
// path differ. Single shared component keeps the surface area small.
import { useEffect, useState } from "react";
import { API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Ruler, Beaker, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { xhrJson } from "../../utils/xhrJson";

function RecipeLookupPage({ activeNav, title, subtitle, Icon, iconBg, iconText, btnBg, slug, idField, testIdPrefix }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/${slug}`, { credentials: "include" });
      setRows(r.ok ? await r.json() : []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!editing?.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const isNew = !editing[idField];
      const url = isNew ? `${API}/admin/${slug}` : `${API}/admin/${slug}/${editing[idField]}`;
      const { ok, data } = await xhrJson(isNew ? "POST" : "PUT", url, { name: editing.name.trim() });
      if (!ok) throw new Error(data.detail || "Save failed");
      toast.success(isNew ? "Created" : "Updated");
      setEditing(null); load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this entry? Recipes that already use it will block deletion.")) return;
    try {
      const { ok, data } = await xhrJson("DELETE", `${API}/admin/${slug}/${id}`);
      if (!ok) throw new Error(data.detail || "Delete failed");
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <AdminLayout activeNav={activeNav}>
      <div className="p-4 sm:p-6 space-y-4" data-testid={`admin-${testIdPrefix}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${iconBg} ${iconText} flex items-center justify-center`}>
              <Icon size={18} />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-extrabold text-gray-900">{title}</h1>
              <p className="text-xs text-gray-500">{subtitle}</p>
            </div>
          </div>
          <Button
            onClick={() => setEditing({ name: "" })}
            className={`${btnBg} text-white`}
            data-testid={`${testIdPrefix}-new-btn`}
          >
            <Plus size={14} className="mr-1" /> New entry
          </Button>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-400"><Loader2 className="animate-spin inline" size={16} /></div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">No entries yet. Create the first one above.</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {rows.map((r) => (
                <li key={r[idField]} className="p-3 sm:p-4 flex items-center justify-between gap-3" data-testid={`${testIdPrefix}-row-${r[idField]}`}>
                  <span className="font-bold text-sm text-gray-900">{r.name}</span>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => setEditing(r)} data-testid={`${testIdPrefix}-edit-${r[idField]}`}><Pencil size={11} /></Button>
                    <Button size="sm" variant="outline" className="h-7 text-[10px] text-red-600 border-red-200" onClick={() => handleDelete(r[idField])} data-testid={`${testIdPrefix}-delete-${r[idField]}`}><Trash2 size={11} /></Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing?.[idField] ? "Edit entry" : "New entry"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Name *</Label>
                <Input
                  value={editing.name || ""}
                  onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. ladle, grams, ml, cup"
                  data-testid={`${testIdPrefix}-form-name`}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className={`${btnBg} text-white`} data-testid={`${testIdPrefix}-form-save`}>
              {saving ? <Loader2 size={13} className="animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

export function AdminRecipeOperationalUnits() {
  return (
    <RecipeLookupPage
      activeNav="recipe-operational-units"
      title="Recipe Operational Units"
      subtitle="Working units cooks use day-to-day — ladle, scoop, cup, portion, tablespoon."
      Icon={Ruler}
      iconBg="bg-indigo-100"
      iconText="text-indigo-700"
      btnBg="bg-indigo-600 hover:bg-indigo-700"
      slug="recipe-operational-units"
      idField="rou_id"
      testIdPrefix="rou"
    />
  );
}

export function AdminRecipeMetricUnits() {
  return (
    <RecipeLookupPage
      activeNav="recipe-metric-units"
      title="Recipe Metric Units"
      subtitle="Standardised measurement units — grams, ml, kg, litres, ounces."
      Icon={Beaker}
      iconBg="bg-cyan-100"
      iconText="text-cyan-700"
      btnBg="bg-cyan-600 hover:bg-cyan-700"
      slug="recipe-metric-units"
      idField="rmu_id"
      testIdPrefix="rmu"
    />
  );
}
